import { isBefore } from 'date-fns';
import { Benefit } from '../@types/benefits';
import { SolvencyRes } from '../@types/productsStatus';
import { DepositState, DEPOSIT_SERVICE_STATUS } from '../@types/deposit';
import { LEGACY_BILLCHECK_STATUS } from '../@types/legacyBillCheck';
import { isDepositClosableNotification } from './isDepositClosableNotification.util';
import { NotificationsResponse } from '../@types/notifications';
import { BILLCHECK_V3_STATUS } from '@/@types/billCheckV3';
import { InsuranceActivationStatus } from '@/@types/insurance';

export enum ICON_TYPE {
  DEFAULT = 'DEFAULT',
  INFO = 'INFO',
  ERROR = 'ERROR',
}

export interface BenefitCardStatus {
  statusTextTranslationKey?: string | null;
  iconType?: ICON_TYPE | null;
}

const STATUS_MAP: Record<InsuranceActivationStatus, BenefitCardStatus> = {
  [InsuranceActivationStatus.ACTIVATED]: {
    iconType: ICON_TYPE.DEFAULT,
  },
  [InsuranceActivationStatus.PENDING]: {
    statusTextTranslationKey: 'benefitCardStatus.insurance.pending',
    iconType: ICON_TYPE.INFO,
  },
  [InsuranceActivationStatus.NOT_ACTIVATED]: {},
};

export const getInsuranceBenefitsStatus = (
  status?: InsuranceActivationStatus,
  isEntitled: boolean = true,
): BenefitCardStatus => {
  if (isEntitled === false) {
    return {};
  }

  return STATUS_MAP[status as InsuranceActivationStatus] || {};
};

export const getCommonBenefitCardStatus = (isBenefitEnabled: boolean): BenefitCardStatus => {
  if (isBenefitEnabled) {
    return {
      statusTextTranslationKey: 'benefitCardStatus.common.activated',
      iconType: ICON_TYPE.DEFAULT,
    };
  }

  return {
    statusTextTranslationKey: 'benefitCardStatus.common.notActivated',
    iconType: ICON_TYPE.INFO,
  };
};

export const getSolvencyBenefitCardStatus = (
  schufa: SolvencyRes | undefined,
  is24Solvency: SolvencyRes | undefined,
  isSchufaStatusError?: boolean,
  is24SolvencyError?: boolean,
): BenefitCardStatus => {
  if (isSchufaStatusError || is24SolvencyError) {
    return { iconType: ICON_TYPE.ERROR };
  }
  if (!schufa?.expirationDate && !is24Solvency?.expirationDate) {
    return {
      iconType: ICON_TYPE.DEFAULT,
    };
  }

  const isSchufaActive = schufa?.expirationDate ? isBefore(new Date(), new Date(schufa.expirationDate)) : false;
  const isIs24SolvencyActive = is24Solvency?.expirationDate
    ? isBefore(new Date(), new Date(is24Solvency?.expirationDate))
    : false;

  if (isSchufaActive || isIs24SolvencyActive) {
    return { statusTextTranslationKey: 'benefitCardStatus.schufa.stored', iconType: ICON_TYPE.DEFAULT };
  } else {
    return { statusTextTranslationKey: 'benefitCardStatus.schufa.expired', iconType: ICON_TYPE.INFO };
  }
};

export const getDepositBenefitCardStatus = (
  deposit: DepositState | undefined,
  notificationData: NotificationsResponse | undefined,
): BenefitCardStatus => {
  if (!deposit?.status) {
    return {};
  }

  const notificationText = getNotificationText(notificationData);

  if (notificationText) {
    return notificationText;
  }

  switch (deposit.status) {
    case DEPOSIT_SERVICE_STATUS.REJECTED:
      return { statusTextTranslationKey: 'benefitCardStatus.deposit.rejected' };
    case DEPOSIT_SERVICE_STATUS.PRE_PENDING:
    case DEPOSIT_SERVICE_STATUS.PENDING:
      return { statusTextTranslationKey: 'benefitCardStatus.deposit.pending' };
    case DEPOSIT_SERVICE_STATUS.ACTIVATED:
      return { statusTextTranslationKey: 'benefitCardStatus.deposit.activated' };
    default:
      return {};
  }
};

export const getNotificationText = (notificationData: NotificationsResponse | undefined): BenefitCardStatus | false => {
  if (!notificationData?.[Benefit.DEPOSIT]?.messages?.length) {
    return false;
  }

  const notifications = notificationData[Benefit.DEPOSIT].messages;

  for (const notification of notifications) {
    if (
      isDepositClosableNotification(notification, 'deposit_cancellation') &&
      notificationData[Benefit.DEPOSIT].marked
    ) {
      return { statusTextTranslationKey: 'benefitCardStatus.deposit.cancelled' };
    }
  }

  for (const notification of notifications) {
    if (isDepositClosableNotification(notification, 'deposit_reduction') && notificationData[Benefit.DEPOSIT].marked) {
      return { statusTextTranslationKey: 'benefitCardStatus.deposit.amountReducted' };
    }
  }

  return false;
};

export const getBillcheckStatus = (
  billcheckStatus: LEGACY_BILLCHECK_STATUS | BILLCHECK_V3_STATUS | undefined,
): BenefitCardStatus => {
  if (!billcheckStatus) {
    return {};
  }

  switch (billcheckStatus) {
    case LEGACY_BILLCHECK_STATUS.DONE:
      return { statusTextTranslationKey: 'benefitCardStatus.billcheck.done' };

    case LEGACY_BILLCHECK_STATUS.ACTION_REQUIRED:
    case BILLCHECK_V3_STATUS.USER_ACTION:
      return { statusTextTranslationKey: 'benefitCardStatus.billcheck.actionRequired', iconType: ICON_TYPE.INFO };
    case BILLCHECK_V3_STATUS.FINISHED:
      return { statusTextTranslationKey: 'benefitCardStatus.billcheck.done' };
    case BILLCHECK_V3_STATUS.PROCESSING:
      return { statusTextTranslationKey: 'benefitCardStatus.billcheck.pending' };
    default:
      return {};
  }
};
